import { Form } from 'antd';
import { useCallback, useState } from 'react';

import type { ModelClientFieldValue } from '@app/api/Client';
import { useSetClientFieldValuesMutation } from '@app/api/Client/api';
import {
  useCreateClientFieldMutation,
  useEditClientFieldMutation,
} from '@app/api/ClientFields';
import type { InlineFieldCreatorValues } from '@app/components/EditClient/components/ClientAdditionalFields/ui/InlineFieldCreator';
import { FRONT_WARNING, SUCCESS, WARNING } from '@app/constants/alerts';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

interface UseClientAdditionalFieldsProps {
  clientId: number | null;
  onFieldsChanged: () => Promise<void> | void;
}

export const useClientAdditionalFields = ({
  clientId,
  onFieldsChanged,
}: UseClientAdditionalFieldsProps) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  // Форма страницы редактирования клиента: значения доп. полей живут в ней
  // под ключами ['ClientFieldValues', FieldId] (компонент рендерится внутри
  // <Form> страницы).
  const pageForm = Form.useFormInstance();

  const [isInlineCreateOpen, setIsInlineCreateOpen] = useState(false);

  const [createClientField, { isLoading: isCreating }] =
    useCreateClientFieldMutation();
  const [editClientField, { isLoading: isTogglingImportance }] =
    useEditClientFieldMutation();
  const [setClientFieldValues, { isLoading: isSavingValue }] =
    useSetClientFieldValuesMutation();

  const openInlineCreate = useCallback(() => setIsInlineCreateOpen(true), []);
  const closeInlineCreate = useCallback(() => setIsInlineCreateOpen(false), []);

  const submitInlineCreate = useCallback(
    async ({ name, isImportant, value }: InlineFieldCreatorValues) => {
      if (!ActiveBranchId) {
        showNotification('Филиал не был распознан', FRONT_WARNING);
        return;
      }

      let createdFieldId: number;
      let createdFieldName: string | null;
      try {
        const created = await createClientField({
          ActiveBranchId,
          data: {
            Id: 0,
            Name: name.trim(),
            IsDeleted: false,
            IsImportant: isImportant,
          },
        }).unwrap();
        createdFieldId = created.Id;
        createdFieldName = created.Name;
      } catch (error) {
        showNotification(error, WARNING);
        return;
      }

      // Введённое значение сразу кладём в форму страницы: даже если запрос
      // значения не пройдёт, оно не потеряется и уйдёт общей кнопкой
      // «Сохранить».
      const trimmedValue = value.trim();
      pageForm.setFieldValue(
        ['ClientFieldValues', String(createdFieldId)],
        trimmedValue
      );

      if (trimmedValue && clientId && createdFieldId) {
        const fieldValue: ModelClientFieldValue = {
          Id: 0,
          FieldId: createdFieldId,
          ClientId: clientId,
          FieldName: createdFieldName,
          Value: trimmedValue,
          IsImportant: isImportant,
        };

        try {
          await setClientFieldValues({
            ActiveBranchId,
            data: { ClientId: clientId, FieldValues: [fieldValue] },
          }).unwrap();
          showNotification('Поле и значение сохранены', SUCCESS);
        } catch (error) {
          showNotification(error, WARNING);
        }
      } else {
        showNotification('Поле создано', SUCCESS);
      }

      setIsInlineCreateOpen(false);
      await onFieldsChanged();
    },
    [
      ActiveBranchId,
      clientId,
      createClientField,
      onFieldsChanged,
      pageForm,
      setClientFieldValues,
      showNotification,
    ]
  );

  const toggleFieldImportance = useCallback(
    async (field: ModelClientFieldValue) => {
      if (!ActiveBranchId || !field.FieldId) {
        showNotification('Филиал не был распознан', FRONT_WARNING);
        return;
      }

      try {
        await editClientField({
          ActiveBranchId,
          data: {
            Id: field.FieldId,
            Name: field.FieldName,
            IsDeleted: false,
            IsImportant: !field.IsImportant,
          },
        }).unwrap();

        await onFieldsChanged();
      } catch (error) {
        showNotification(error, WARNING);
      }
    },
    [ActiveBranchId, editClientField, onFieldsChanged, showNotification]
  );

  return {
    isInlineCreateOpen,
    isCreating: isCreating || isSavingValue,
    isTogglingImportance,
    openInlineCreate,
    closeInlineCreate,
    submitInlineCreate,
    toggleFieldImportance,
  };
};
